<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <h1 class="stage-page-title">{{ $t('CMS-Seiten') }}</h1>
                </div>
                <div class="stage-page-header-actions">
                    <router-link class="btn-stage-icon-plain" :title="$t('Back')" :aria-label="$t('Back')" to="/workspace/cms">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrow-left"></svg-vue>
                    </router-link>
                    <router-link class="btn-stage-icon-plain" :title="$t('Seite anlegen')" :aria-label="$t('Seite anlegen')" to="/workspace/cms/pages/new">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.plus"></svg-vue>
                    </router-link>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow sm:rounded relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>

                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <div class="h-full overflow-auto">
                        <div v-if="pages.length > 0" class="hidden sm:block">
                            <div class="align-middle inline-block min-w-full border-b border-ultralight">
                                <table class="table-listview table-listview--zebra">
                                    <thead>
                                    <tr>
                                        <th class="table-listview-l-th">{{ $t('Seite') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-40">{{ $t('Template') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-24">{{ $t('Pfade') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-48">{{ $t('Erstellt von') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-48">{{ $t('Letzte Veröffentlichung') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-40">{{ $t('Update') }}</th>
                                        <th class="table-listview-l-th table-listview-l-th-right whitespace-nowrap w-24">{{ $t('Aktionen') }}</th>
                                    </tr>
                                    </thead>
                                    <tbody class="bg-white">
                                    <tr
                                        v-for="page in pages"
                                        :key="page.id"
                                        class="cursor-pointer"
                                        @click="$router.push('/workspace/cms/pages/' + page.id + '/edit')"
                                    >
                                        <td class="table-listview-l-td whitespace-nowrap">
                                            <div class="flex items-center min-w-0">
                                                <div class="mr-3 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-700">
                                                    <svg-vue class="w-8 h-8 p-1.5 text-gray-200" icon="font-awesome.regular.copy"></svg-vue>
                                                </div>
                                                <div class="min-w-0">
                                                    <div class="flex items-center gap-2 min-w-0">
                                                        <div class="text-sm font-medium text-gray-900 truncate">{{ page.name }}</div>
                                                        <span :class="pageStatusMeta(page).dotClass" class="h-2 w-2 rounded-full flex-shrink-0"></span>
                                                    </div>
                                                    <div v-if="!page.published_at" class="text-xs text-gray-500 mt-1">
                                                        {{ $t('Noch nicht veröffentlicht') }}
                                                    </div>
                                                    <div v-else-if="page.has_unpublished_changes" class="text-xs text-amber-600 mt-1">
                                                        {{ $t('Unveröffentlichte Änderungen') }}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap text-sm text-gray-700">
                                            {{ page.template ? page.template.name : '-' }}
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap text-sm text-gray-700">
                                            {{ page.routes_count }}
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap">
                                            <div class="flex items-center min-w-0 max-w-[12rem] text-sm text-gray-500">
                                                <img
                                                    v-if="createdActorAvatar(page)"
                                                    :src="createdActorAvatar(page)"
                                                    :alt="createdActorName(page)"
                                                    :title="createdActorName(page)"
                                                    class="h-6 w-6 rounded-full border border-white object-cover mr-2 flex-shrink-0"
                                                >
                                                <span class="truncate text-gray-900">{{ createdActorName(page) }}</span>
                                            </div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap">
                                            <div v-if="page.published_by_user" class="flex items-center min-w-0 max-w-[12rem] text-sm text-gray-500">
                                                <img
                                                    :src="publishedActorAvatar(page)"
                                                    :alt="publishedActorName(page)"
                                                    :title="publishedActorName(page)"
                                                    class="h-6 w-6 rounded-full border border-white object-cover mr-2 flex-shrink-0"
                                                >
                                                <span class="truncate text-gray-900">{{ publishedActorName(page) }}</span>
                                            </div>
                                            <div v-else class="text-sm text-gray-500">-</div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap text-sm text-gray-500">
                                            {{ page.updated_at ? $momentFormatDateTimeAgo(page.updated_at) : '-' }}
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap text-right" @click.stop>
                                            <div v-on-clickaway="closeActionMenu" class="relative inline-flex" @click.stop>
                                                <button
                                                    class="inline-flex items-center justify-center text-gray-400 hover:text-gray-700 focus:outline-none"
                                                    type="button"
                                                    :title="$t('Aktionen')"
                                                    :aria-label="$t('Aktionen')"
                                                    @click.stop="toggleActionMenu(page.id)"
                                                >
                                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                                                </button>
                                                <div
                                                    v-show="actionMenuPageId === page.id"
                                                    class="absolute right-0 top-full z-20 mt-2 w-48 rounded border border-bordergray-100 bg-white stage-dropdown-menu shadow-lg"
                                                >
                                                    <button
                                                        class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                        type="button"
                                                        @click.stop="openDuplicateDialog(page)"
                                                    >
                                                        {{ $t('Duplizieren') }}
                                                    </button>
                                                    <button
                                                        class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                        type="button"
                                                        @click.stop="openDeleteDialog(page)"
                                                    >
                                                        {{ $t('Löschen') }}
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div v-else-if="!loading" class="h-full">
                            <empty-state />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <confirm-dialog
            :open="confirmDialog.open"
            :title="confirmDialog.title"
            :text="confirmDialog.text"
            :confirm-label="confirmDialog.confirmLabel"
            :confirm-button-class="confirmDialog.confirmButtonClass"
            headline-id="cms-pages-list-confirm-headline"
            @close="closeConfirmDialog"
            @confirm="confirmDialogAction"
        />
    </div>
</template>

<script>
import ConfirmDialog from "@/components/modals/confirm-dialog";
import {
    cmsCreatedActorAvatar,
    cmsCreatedActorName,
    cmsPublishedActorAvatar,
    cmsPublishedActorName,
} from '@/utils/cms-update-meta';
import {cmsPublicationStatusMeta} from '@/utils/cms-status-badge';

export default {
    name: "cms-pages-list",
    components: {
        ConfirmDialog,
    },
    data() {
        return {
            loading: true,
            pages: [],
            actionMenuPageId: null,
            confirmDialog: {
                open: false,
                title: '',
                text: '',
                confirmLabel: '',
                confirmButtonClass: 'btn btn-red mr-2 sm:mr-0',
                pendingAction: null,
            },
        };
    },
    mounted() {
        this.loadPages();
    },
    methods: {
        createdActorAvatar(page) {
            return cmsCreatedActorAvatar(page);
        },
        createdActorName(page) {
            return cmsCreatedActorName(page);
        },
        publishedActorAvatar(page) {
            return cmsPublishedActorAvatar(page);
        },
        publishedActorName(page) {
            return cmsPublishedActorName(page);
        },
        pageStatusMeta(page) {
            return cmsPublicationStatusMeta(this, page);
        },
        toggleActionMenu(pageId) {
            this.actionMenuPageId = this.actionMenuPageId === pageId ? null : pageId;
        },
        closeActionMenu() {
            this.actionMenuPageId = null;
        },
        openDuplicateDialog(page) {
            this.closeActionMenu();
            this.confirmDialog = {
                open: true,
                title: this.$t('Seite duplizieren'),
                text: this.$t('Soll für diese Seite eine vollständige Kopie angelegt werden?'),
                confirmLabel: this.$t('Duplizieren'),
                confirmButtonClass: 'btn btn-gray mr-2 sm:mr-0',
                pendingAction: () => this.confirmDuplicatePage(page),
            };
        },
        openDeleteDialog(page) {
            this.closeActionMenu();
            this.confirmDialog = {
                open: true,
                title: this.$t('Seite löschen'),
                text: this.$t('Soll die Seite inklusive aller verknüpften Inhalte endgültig gelöscht werden?'),
                confirmLabel: this.$t('Löschen'),
                confirmButtonClass: 'btn btn-red mr-2 sm:mr-0',
                pendingAction: () => this.deletePage(page),
            };
        },
        closeConfirmDialog() {
            this.confirmDialog = {
                open: false,
                title: '',
                text: '',
                confirmLabel: '',
                confirmButtonClass: 'btn btn-red mr-2 sm:mr-0',
                pendingAction: null,
            };
        },
        confirmDialogAction() {
            const action = this.confirmDialog.pendingAction;
            this.closeConfirmDialog();
            if (typeof action === 'function') {
                action();
            }
        },
        confirmDuplicatePage(page) {
            this.loading = true;
            axios.post('api/workspace/admin/cms/pages/' + page.id + '/duplicate').then((response) => {
                const duplicatedPageId = response?.data?.page?.id || null;
                if (duplicatedPageId) {
                    this.$router.push('/workspace/cms/pages/' + duplicatedPageId + '/edit');
                    return;
                }

                this.loadPages();
            }).catch(() => {
                this.loading = false;
            });
        },
        deletePage(page) {
            this.loading = true;
            axios.delete('api/workspace/admin/cms/pages/' + page.id).then(() => {
                this.loadPages();
            }).catch(() => {
                this.loading = false;
            });
        },
        loadPages() {
            this.loading = true;
            axios.get('api/workspace/admin/cms/pages').then((response) => {
                this.pages = Array.isArray(response.data) ? response.data : [];
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        },
    },
}
</script>
